import React from 'react';
import { StarIcon, CheckBadgeIcon } from '@heroicons/react/24/solid';

interface Vendor {
  name: string;
  services: string[];
  rating: number;
  reviews: number;
  verified: boolean;
  logo: string;
  description?: string;
}

interface VendorProfileCardProps {
  vendor: Vendor;
  onRequestQuote?: (vendor: Vendor) => void;
}

const VendorProfileCard: React.FC<VendorProfileCardProps> = ({ vendor, onRequestQuote }) => {
  const handleRequestQuote = () => {
    if (onRequestQuote) {
      onRequestQuote(vendor);
    } else {
      // Default behavior - show alert
      alert(`Requesting quote from ${vendor.name}`);
    }
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <StarIcon key={i} className="w-4 h-4 text-yellow-400 fill-current" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <div key="half" className="relative w-4 h-4">
          <StarIcon className="w-4 h-4 text-gray-600 fill-current absolute" />
          <div className="overflow-hidden w-1/2">
            <StarIcon className="w-4 h-4 text-yellow-400 fill-current" />
          </div>
        </div>
      );
    }

    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(
        <StarIcon key={`empty-${i}`} className="w-4 h-4 text-gray-600 fill-current" />
      );
    }

    return stars;
  };

  return (
    <div className="group glass-card bg-[#1a1a1d] backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:scale-[1.02] hover:shadow-2xl hover:shadow-[#22d3ee]/20 transition-all duration-300 animate-fade-in">
      {/* Header with Logo and Verified Badge */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <img
              src={vendor.logo}
              alt={`${vendor.name} logo`}
              className="w-16 h-16 rounded-full object-cover border-2 border-white/20 group-hover:border-[#22d3ee]/50 transition-colors duration-300"
              onError={(e) => {
                // Fallback to gradient placeholder if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const placeholder = target.nextElementSibling as HTMLElement;
                if (placeholder) {
                  placeholder.style.display = 'flex';
                }
              }}
            />
            {/* Fallback gradient placeholder */}
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#22d3ee] to-[#38bdf8] flex items-center justify-center text-white font-bold text-lg hidden">
              {vendor.name.charAt(0)}
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white group-hover:text-[#22d3ee] transition-colors duration-300">
              {vendor.name}
            </h3>
            <p className="text-sm text-white/60 mt-1">
              {vendor.description || `Professional ${vendor.services[0]} services`}
            </p>
          </div>
        </div>
        
        {vendor.verified && (
          <div className="flex-shrink-0">
            <div className="relative">
              <CheckBadgeIcon className="w-8 h-8 text-[#22d3ee] drop-shadow-lg" />
              <div className="absolute inset-0 bg-[#22d3ee]/20 rounded-full blur-md animate-pulse"></div>
            </div>
          </div>
        )}
      </div>

      {/* Services Tags */}
      <div className="flex flex-wrap gap-2 mb-4">
        {vendor.services.map((service, index) => (
          <span
            key={index}
            className={`
              px-3 py-1 rounded-full text-xs font-medium transition-all duration-200
              ${service.toLowerCase().includes('preferred') || service.toLowerCase().includes('insured')
                ? 'bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 text-[#22d3ee] border border-[#22d3ee]/30'
                : 'bg-white/10 text-white/80 border border-white/20'
              }
              hover:scale-105 hover:bg-white/20
            `}
          >
            {service}
          </span>
        ))}
      </div>

      {/* Rating */}
      <div className="flex items-center space-x-2 mb-6">
        <div className="flex items-center space-x-1">
          {renderStars(vendor.rating)}
        </div>
        <span className="text-sm font-medium text-white">
          {vendor.rating}
        </span>
        <span className="text-sm text-white/60">
          ({vendor.reviews} review{vendor.reviews !== 1 ? 's' : ''})
        </span>
      </div>

      {/* Request Quote Button */}
      <button
        onClick={handleRequestQuote}
        className="
          w-full btn-primary
          relative overflow-hidden
          group/button
        "
      >
        <span className="relative z-10 flex items-center justify-center">
          Request Quote
          <svg 
            className="w-4 h-4 ml-2 group-hover/button:translate-x-1 transition-transform duration-200" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </span>
        
        {/* Animated background glow */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#22d3ee] to-[#38bdf8] opacity-0 group-hover/button:opacity-100 transition-opacity duration-300"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#22d3ee]/50 to-[#38bdf8]/50 blur-xl opacity-0 group-hover/button:opacity-75 transition-opacity duration-300 -z-10"></div>
      </button>
    </div>
  );
};

export default VendorProfileCard; 